<template>
    <div
        id="app"
        class="new-top-level-nav"
        :class="[dynamicClass, { 'new-sport-nav': true }]"
    >
        <header
            v-if="isMobileLayout"
            class="mobile-header-wrapper"
        >
            <router-view
                name="mobileHeader"
                v-resize-self="onHeaderResize"
            />
            <router-view
                ref="mobileSubnavRef"
                name="mobileSubnav"
                :class="mobileSubnavClass"
                v-resize-self="onSubnavResize"
            />
        </header>
        <router-view
            v-else
            name="header"
        />
        <template v-if="isMobileLayout">
            <template>
                <div
                    :class="{
                        [mobileOverlayContainerClass]: true,
                    }"
                >
                    <router-view
                        name="rightSidebarMobile"
                        class="right-sidebar mobile-sidebar"
                    />
                    <SnackbarNotifications />
                </div>
                <div class="mobile-bottom-sticky-content">
                    <router-view name="mobileBottomContent" />
                    <router-view name="mobileBottomSticky" />
                </div>
            </template>
            <router-view
                v-if="!mainClassConfig.isFullScreen"
                name="mobileBottomNav"
            />
        </template>
        <div class="app-main">
            <template v-if="!isMobileLayout">
                <template>
                    <router-view
                        name="leftSidebar"
                        class="left-sidebar"
                    />
                    <router-view
                        name="rightSidebar"
                        class="right-sidebar"
                    />
                </template>
                <router-view
                    name="subnav"
                    class="subnav"
                />
            </template>
            <template>
                <main class="main">
                    <router-view name="warning" />
                    <SnackbarNotifications />
                    <AlertNotifications />
                    <router-view name="content" />
                </main>
                <router-view
                    name="tickets"
                    class="other"
                />
            </template>
            <FloatingDepositButton />
            <PE113FloatingPlaceBetButton />
        </div>
        <router-view
            name="footer"
            class="other"
        />
        <div id="zendesk-loading">
            <SpinnerBars />
        </div>
        <router-view name="overlay" />
        <FeatureTestingDisclaimer
            v-if="showFeatureTestingDisclaimer"
        />
    </div>
</template>

<script>
    import SnackbarNotifications from '@app/modules/notifications/SnackbarNotifications.vue';
    import AlertNotifications from '../modules/alerts/AlertsContainer.vue';
    import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';
    import { mapGetters } from 'vuex';

    import { mainClassConfig } from '@app/utils/classToggler';
    import FeatureTestingDisclaimer from '../modules/featureTesting/FeatureTestingDisclaimer.vue';
    import { showFeatureTestingDisclaimer } from '../utils/featureTesting';
    import FloatingDepositButton from '@shared/FloatingDepositButton.vue';

    export default {
        data() {
            return {
                lastHeaderHeight: null,
                lastSubnavHeight: null,
                lastRouteName: null,
            };
        },
        created() {
            this.mainClassConfig = mainClassConfig;
        },
        computed: {
            ...mapGetters('ui/sportOffer/sports', [
                'selectedTreeItems',
            ]),
            ...mapGetters('navigation', [
                'isOnPrematchPage',
            ]),
            ...mapGetters('ui/sportOffer', [
                'expandedEvent',
            ]),
            ...mapGetters('navigation', [
                'isOnOfferEventPage',
            ]),
            isMobileLayout() {
                return isMobileLayout();
            },
            dynamicClass() {
                const layoutClass = this.$route.meta && this.$route.meta.layoutClass;
                if (typeof layoutClass === 'function') {
                    return layoutClass(this.$route);
                }
                if (typeof layoutClass === 'string') {
                    return layoutClass;
                }
                return '';
            },
            mobileSubnavClass() {
                if (!this.expandedEvent) {
                    return '';
                }
                return 'mobile-subnav';
            },
            mobileOverlayContainerClass() {
                return this.isOnOfferEventPage
                    ? 'mobile-overlay-container sports-nav w-padding'
                    : 'mobile-overlay-container sports-nav';
            },
            showFeatureTestingDisclaimer() {
                return showFeatureTestingDisclaimer.showDisclaimer;
            },
        },
        methods: {
            onHeaderResize({ height }) {
                // desperate attempt to guess inital header height: this assumes that initial
                // header is also the biggest one
                if ((this.lastRouteName !== this.$route.name || height > this.lastHeaderHeight) && height !== 0) {
                    document.documentElement.style.setProperty('--initial-mobile-header-height', `${height}px`);
                    this.lastRouteName = this.$route.name;
                }
                if (height !== this.lastHeaderHeight && height !== 0) {
                    document.documentElement.style.setProperty('--mobile-header-height', `${height}px`);
                    this.lastHeaderHeight = height;
                }
            },
            onSubnavResize({ height }) {
                const hasMobileSubnav = this.$refs.mobileSubnavRef?.$children?.length > 0;

                if (!hasMobileSubnav) {
                    if (this.lastSubnavHeight !== 0) {
                        document.documentElement.style.setProperty('--mobile-subnav-height', '0px');
                        this.lastSubnavHeight = 0;
                    } else {
                        return;
                    }
                }
                if (height === this.lastSubnavHeight || height === 0) {
                    return;
                }
                document.documentElement.style.setProperty('--mobile-subnav-height', `${height}px`);
                this.lastSubnavHeight = height;
            },
        },
        components: {
            SnackbarNotifications,
            AlertNotifications,
            FeatureTestingDisclaimer,
            FloatingDepositButton,
            PE113FloatingPlaceBetButton: () => import('@shared/PE113FloatingPlaceBetButton.vue'),
        },
    };
</script>

<style lang="scss" scoped>
    @import '~@superbet-group/web.lib.global-styles/config';

    .mobile-sidebar {
        height: calc(var(--vh, 1vh) * 100 - (#{$mobile-bottom-nav-height} + env(safe-area-inset-bottom)) - var(--mobile-header-height));
        height: calc(100dvh - (#{$mobile-bottom-nav-height} + env(safe-area-inset-bottom)) - var(--mobile-header-height));
    }

    .mobile-header-wrapper {
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 501;
    }

</style>
